import React, { useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  BUTTON_SIZE,
  COLORS,
  DIVIDER_SIZE,
  TEXT_ALIGN,
  USER_ROLES,
} from '@helpers/constants'
import Divider from '@components/Divider'
import BreadCrumbs from '@components/BreadCrumbs'
import Button from '@components/Button'
import Text from '@components/Text'
import Tooltip from '@components/Tooltip'
import { AppContext } from '@state/context'
import { setCurrentSideBarWidth } from '@state/actions/panelsActions'
import { useBreadcrumbs } from '@hooks/useBreadcrumbs'
import { useExamsAllowed } from '@hooks/useExamsAllowed'
import Icon from '@components/Icon'
import { getSectionTagIcon } from '@helpers/exams'
import SectionProgressButtons from '@components/SectionProgressButtons'

const SideBar = (props) => {
  const { breadcrumb, children } = props
  useBreadcrumbs(breadcrumb)
  const [, dispatch] = useContext(AppContext)
  const ref = useRef()

  const [contentWidth, setContentWidth] = useState(0)
  const debounceTimeout = useRef()

  const updateWidths = () => {
    if (ref.current) {
      const width = ref.current.getBoundingClientRect()?.right || 0
      const clientWidth = ref.current.clientWidth || 0
      dispatch(setCurrentSideBarWidth(width))
      setContentWidth(clientWidth)
    }
  }

  const handleResize = () => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }
    debounceTimeout.current = window.setTimeout(() => {
      updateWidths()
    }, 80)
  }

  useEffect(() => {
    updateWidths()

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
  }, [])

  // const width = ref?.current?.getBoundingClientRect()?.right
  // const contentWidth = ref?.current?.clientWidth

  // useEffect(() => {
  //   dispatch(setCurrentSideBarWidth(width))
  // }, [width])

  return (
    <aside ref={ref}>
      <div
        className={'fixed'}
        style={{ width: contentWidth + 'px', height: 'calc(100vh - 80px)' }}
      >
        <div className={'overflow-y-auto pr-8 pb-8 h-full'}>
          {/* This div prevents the left-most area from scrolling the main content */}
          <div className={'absolute top-0 right-[100%] w-full h-full'} />

          <div>
            <BreadCrumbs />
          </div>

          <Divider
            size={DIVIDER_SIZE.LINE}
            color={COLORS.LIGHT_DARK_GREY.BORDER}
            line
          />
          {children}
        </div>
      </div>
    </aside>
  )
}

SideBar.propTypes = {
  breadcrumb: PropTypes.object,
}

SideBar.SectionList = (props) => {
  const {
    sections,
    currentSection,
    subject,
    onClick,
    disabled,
    sectionsOnResource,
    resource,
    resourceType,
  } = props

  const [state] = useContext(AppContext)
  const { userRole } = state?.app || {}
  const ref = useRef()
  const [showTooltip, setShowTooltip] = useState(false)

  const examsAllowed = useExamsAllowed()

  return (
    <>
      {sections.length > 0 ? (
        <div
          className={'flex flex-col my-4 items-start'}
          ref={ref}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {/* No exams allowed */}
          {!examsAllowed && (
            <SectionList
              userRole={userRole}
              sections={sections}
              currentSection={currentSection}
              subject={subject}
              onClick={onClick}
            />
          )}

          {/* Exams allowed, check what to show depending on userRole */}
          {examsAllowed && (
            <>
              {userRole === USER_ROLES.STUDENT ? (
                <StudentSectionList
                  sections={sections}
                  currentSection={currentSection}
                  onClick={onClick}
                />
              ) : (
                <SectionList
                  userRole={userRole}
                  sections={sections}
                  currentSection={currentSection}
                  subject={subject}
                  onClick={onClick}
                  sectionsOnResource={sectionsOnResource}
                  resource={resource}
                  resourceType={resourceType}
                />
              )}
            </>
          )}

          {disabled && (
            <Tooltip
              referenceElement={ref.current}
              show={showTooltip}
              color={COLORS.LIGHT_LIGHT_GREY}
            >
              Tryck på förhandsgranskning för att gå till ett annat avsnitt
            </Tooltip>
          )}
        </div>
      ) : (
        <>
          <Divider size={DIVIDER_SIZE.SMALL} />
          <Text color={COLORS.WHITE.TEXT}>Inga avsnitt finns i boken än</Text>
          <Divider size={DIVIDER_SIZE.SMALL} />
        </>
      )}
    </>
  )
}

// --- Section list ---
const SectionList = (props) => {
  const {
    userRole,
    sections,
    currentSection,
    subject,
    onClick,
    sectionsOnResource,
    resource,
    resourceType,
  } = props

  return (
    <>
      {sections.map((section, index) => {
        const currentSectionOnResource =
          sectionsOnResource && sectionsOnResource.length > 0
            ? sectionsOnResource[index]
            : null
        return (
          <div
            key={section.id}
            className='flex items-center justify-between self-stretch'
          >
            <Button
              classes='!whitespace-normal'
              textColor={
                currentSection?.id === section.id
                  ? `text-subject-${subject?.slug}-light`
                  : `text-mediumGrey`
              }
              icon={'SectionFilled'}
              iconLeft
              size={BUTTON_SIZE.NOSIZE}
              handleClick={() => {
                onClick(section)
              }}
            >
              <Text
                color={COLORS.WHITE.TEXT}
                align={TEXT_ALIGN.LEFT}
                bold={currentSection?.id === section.id}
              >
                {section.name}
                {userRole === USER_ROLES.ADMIN && !section.published && (
                  <span className={COLORS.ALERT_RED.TEXT}> UTKAST</span>
                )}
              </Text>
            </Button>

            {resource && currentSectionOnResource && (
              <SectionProgressButtons
                section={currentSectionOnResource}
                resource={resource}
                resourceType={resourceType}
              />
            )}
          </div>
        )
      })}
    </>
  )
}

// --- Student section list with exams functionality ---
const StudentSectionList = (props) => {
  const { sections, currentSection, onClick } = props

  const sectionIsUnlocked = (index) => {
    if (index === 0) return true // Allow navigation to the first section

    // Check if all previous sections have their exams completed
    for (let i = 0; i < index; i++) {
      const section = sections[i]
      const allExamsCompleted = section.exams.every((exam) => {
        const passedAttempt = exam.customerAttempts.some(
          (attempt) => attempt.isPassed === true
        )
        const maxAttemptsReached =
          exam.maxAttempts !== null &&
          exam.customerAttempts.length >= exam.maxAttempts
        return passedAttempt || maxAttemptsReached
      })

      // If any previous section has exams that are not completed, return false
      if (!allExamsCompleted) return false
    }

    // All previous sections have their exams completed
    return true
  }

  return (
    <>
      {sections.map((section, index) => {
        const unlocked = sectionIsUnlocked(index)
        const icon = getSectionTagIcon(section.exams)

        return (
          <Button
            key={section.id}
            classes={`!whitespace-normal ${!unlocked ? 'opacity-50' : ''}`}
            textColor={'text-mediumGrey'}
            icon={icon}
            iconLeft
            size={BUTTON_SIZE.NOSIZE}
            handleClick={() => {
              if (unlocked) {
                onClick(section)
              }
            }}
          >
            <div className={`flex justify-start items-center gap-x-1`}>
              <Text
                color={COLORS.WHITE.TEXT}
                align={TEXT_ALIGN.LEFT}
                bold={currentSection?.id === section.id}
              >
                {section.name}
              </Text>

              {!unlocked && (
                <Icon
                  icon={'PadLock'}
                  color={COLORS.WHITE.TEXT}
                  iconClasses={'w-4 h-4'}
                />
              )}
            </div>
          </Button>
        )
      })}
    </>
  )
}

SideBar.SectionList.propTypes = {
  onClick: PropTypes.func.isRequired,
  sections: PropTypes.array.isRequired,
  currentSection: PropTypes.object,
  subject: PropTypes.object,
}

export default SideBar
